import React, { useState, useEffect, useRef } from 'react';
import { withRouter, Link, RouteComponentProps } from 'react-router-dom';
import MaterialTable, {
  MaterialTableProps,
  MTableBody,
  MTableHeader,
  MTableToolbar,
} from 'material-table';
import {
  Button,
  Grid,
  TableBody,
  TableRow,
  TableCell,
  MuiThemeProvider,
  Tooltip,
  createMuiTheme,
  MenuItem,
  Typography,
  Box
} from '@material-ui/core';
import Axios from 'axios';
import { MoreVert as MoreIcon } from '@material-ui/icons';

import { Snackbar } from '../../../components/Snackbar';
import { SnackbarProps } from '../../../components/Snackbar/Snackbar';
import AdminLayout from '../../../layouts/AdminLayout';
import Course from '../../../models/Course';
import { CloneAssignmentSpecsModal } from '../../../components/Modal';
import auth from '../../../libs/auth';
import { useDialog } from '../../../context/Dialog';
import { MenuIconButton } from '../../../components/Buttons';
import utils from '../../../libs/utils';

const Courses = (props: RouteComponentProps) => {
  const [initialized, setInitialized] = useState<boolean>(false);
  const [courses, setCourses] = useState<Course[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [selectedCourseIDs, setSelectedCourseIDs] = useState<string[]>([]);
  const [registeredCourses, setRegisteredCourses] = useState<string[]>([]);
  const [showSnackbar, setSnackbarVisibility] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [snackbarType, setSnackbarType] = useState<SnackbarProps['type']>();
  const [cloneSourceCourse, setCloneSourceCourse] = useState<Course | null>(null);
  const [cloneTargetCourse, setCloneTargetCourse] = useState<Course | null>(null);
  const tableRef = useRef(null as any);

  const dialog = useDialog();

  const theme = createMuiTheme({
    palette: {
      secondary: {
        light: '#ffffff',
        main: '#000000',
      },
    },
  });

  const doneCloneModalHandler = async () => {
    setCloneSourceCourse(null);
    setCloneTargetCourse(null);
    await retryHandler();
  };

  const registerHandler = async () => {
    setIsLoading(true);

    try {
      const result = await Promise.all(
        selectedCourseIDs.map((id) => {
          return Axios.post('/admin/course-instance', {
            	id: id+''
          	});
        })
      );

      showSuccessAlert(`${result.length} Course Registered!`);
    } catch (e) {
      showErrorAlert(utils.getRequestErrorMessage(e));
    } finally {
      tableRef?.current?.onAllSelected(false);
      setIsLoading(false);
      await loadRegisteredCourses();
    }
  };

  const unregisterHandler = async () => {
    setIsLoading(true);

    try {
      const result = await Promise.all(
        selectedCourseIDs.map((id) =>
          Axios.delete('/admin/course-instance', {
            data: { id: id+''},
          })
        )
      );

      showSuccessAlert(`${result.length} Course Unregistered!`);
    } catch (e) {
      showErrorAlert(utils.getRequestErrorMessage(e));
    } finally {
      tableRef?.current?.onAllSelected(false);
      setIsLoading(false);
      await loadRegisteredCourses();
    }
  };

  const showSuccessAlert = (message: string) => {
    setSnackbarType('success');
    setSnackbarMessage(message);
    setSnackbarVisibility(true);
  };

  const showErrorAlert = (message: string) => {
    setSnackbarType('error');
    setSnackbarMessage(message);
    setSnackbarVisibility(true);
  };

  const retryHandler = async () => {
    try {
      setIsLoading(true);
      setErrorMessage('');
      await loadCourses();

      // if (auth.isAdmin()) {
      //   await loadRegisteredCourses();
      // }
    } catch (e) {
      setErrorMessage(utils.getRequestErrorMessage(e));
    } finally {
      setIsLoading(false);
    }
  };

  const renderToolbar = (props: any) => (
    <div>
      <MuiThemeProvider theme={theme}>
        <MTableToolbar {...props} />
      </MuiThemeProvider>
      <Box px={3}>
        <Grid container spacing={2}>
          {(() => {
            if (cloneSourceCourse)
              return (
                <>
                  <Grid item>
                    <Typography variant="h6">
                      Select course for cloning specs of {cloneSourceCourse.name}(
                      {cloneSourceCourse.id})
                    </Typography>
                  </Grid>

                  <Grid item>
                    <Tooltip title="Cancel Cloning.">
                      <Button
                        type="button"
                        size="small"
                        variant="contained"
                        color="secondary"
                        onClick={async (event) => {
                          event.stopPropagation();
                          setCloneSourceCourse(null);
                        }}>
                        Cancel
                      </Button>
                    </Tooltip>
                  </Grid>
                </>
              );

            if (auth.isAdmin())
              return (
                <>
                  <Grid item>
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={!props.selectedRows.length || !!cloneSourceCourse}
                      onClick={async (event) => {
                        event.stopPropagation();
                        if (await dialog.confirm('Confirm Registration', {
                          title: 'Confirm'
                        })) await registerHandler();
                      }}
                    >
                      Register
                    </Button>
                  </Grid>

                  <Grid item>
                    <Button
                      variant="contained"
                      color="secondary"
                      disabled={!props.selectedRows.length || !!cloneSourceCourse}
                      onClick={async (event) => {
                        event.stopPropagation();
                        if (await dialog.confirm('Confirm Unregistration', {
                          title: 'Confirm'
                        })) await unregisterHandler();
                      }}
                    >
                      Unregister
                    </Button>
                  </Grid>
                </>
              );
          })()}
        </Grid>
      </Box>
    </div>
  );

  const renderHeader = (props: any) => {

    if (!auth.isAdmin() && !!props.columns && props.columns.length >= 3) {
      props.columns.pop();
    }

    return (
      <MuiThemeProvider theme={theme}>
        <MTableHeader {...props} />
      </MuiThemeProvider>
    );
  };

  const renderBody = (props: any) => (
    <MuiThemeProvider theme={theme}>
      {!!errorMessage ? (
        <TableBody>
          <TableRow>
            <TableCell colSpan={4} align="center">
              <div className="text-center p-5">
                <h2 className="m-0">Error Loading Courses</h2>
                <p>{errorMessage}</p>
                <Button
                  type="button"
                  onClick={retryHandler}
                  color="primary"
                  className="mt-3"
                  variant="contained"
                >
                  Try Again
                </Button>
              </div>
            </TableCell>
          </TableRow>
        </TableBody>
      ) : (
        <MTableBody {...props} />
      )}
    </MuiThemeProvider>
  );

  const tableProps: MaterialTableProps<Course> = {
    title: 'Courses',
    data: courses,
    isLoading,
    columns: [
      {
        title: 'ID',
        field: 'id',
        width: '10%',
      },
      {
        title: 'Name',
        field: 'fullname',
        render(rowData) {
          if (cloneSourceCourse)
            return rowData.fullname;
          return (
            <Link to={`/admin/courses/${rowData.id}/assignments`}>
              {rowData.fullname}
            </Link>
          );
        }
      },
      {
        title: 'Status',
        field: 'status',
        hidden: !auth.isAdmin(),
        defaultSort: auth.isAdmin() ? 'asc' : undefined,
      },
      {
        title: 'Actions',
        render: (course) => {
          if (cloneSourceCourse) {
            if (course !== cloneSourceCourse)
              return (
                <Button
                  type="button"
                  size="small"
                  color="primary"
                  variant="outlined"
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    setCloneTargetCourse(course);
                  }}>
                  Select
                </Button>
              );

            return '';
          }

          return (
            <MenuIconButton icon={<MoreIcon />}>
              {({setAnchorEl}) => [
                <MenuItem
                  key="clone-specs"
                  onClick={() => {
                    setAnchorEl(null);
                    setCloneSourceCourse(course);
                  }}>
                  Clone Course Specs
                </MenuItem>,
                <MenuItem
                  key="view-reports"
                  component="a"
                  href={`#/admin/reports/${course.id}/${course.name}`}>
                  View Reports
                </MenuItem>
              ]}
            </MenuIconButton>
          );
        }
      }
    ],
    localization: {
      body: {
        emptyDataSourceMessage: 'No Course/s Found.',
      },
    },
    options: {
      headerStyle: {
        fontWeight: 'bold',
      },
      pageSize: 10,
      pageSizeOptions: [5, 10, 20, 50],
      emptyRowsWhenPaging: false,
      search: true,
      selection: (() => {
        // don't show selection if there's a selected clone source
        if (cloneSourceCourse)
          return false;

        // show selection if user is admin only
        return Boolean(auth.isAdmin());
      })(),
      sorting: true,
      draggable: false,
      showEmptyDataSourceMessage: initialized && !isLoading,
      thirdSortClick: false,
    },
    components: {
      Toolbar: renderToolbar,
      Header: renderHeader,
      Body: renderBody,
    },
    onSelectionChange: (selectedRows) => {
      const selectedCourseIDs: any = selectedRows.map(({ id }) => id);

      setSelectedCourseIDs(selectedCourseIDs);
    },
    onRowClick: cloneSourceCourse ? undefined : (event, rowData) =>
      props.history.push(`/admin/courses/${rowData?.id}/assignments`),
  };

  const loadCourses = async (params = {}) => {
    const session = auth.getSession();

    const fetchedCourses = await Course.getMoodleCourses({
      token: session?.accessToken,
    });
    
    setCourses(fetchedCourses);
  };

  const loadRegisteredCourses = async () => {
    const data = (await Axios.get('/admin/course-instances')).data || [];

    setRegisteredCourses(data);
  };

  const initializeData = async () => {
    try {
      setIsLoading(true);
      setErrorMessage('');

      await loadCourses();

      if (auth.isAdmin()) {
        await loadRegisteredCourses();
      }
    } catch (e) {
      setErrorMessage(utils.getRequestErrorMessage(e));
    } finally {
      setIsLoading(false);
      setInitialized(true);
    }
  };

  useEffect(() => {
    if (!initialized) {
      initializeData();
    }
  }, [initialized]);

  useEffect(() => {
    if (courses && !!courses.length) {
      const result = courses.reduce(
        (acc: any, course) => [
          ...acc,
          {
            ...course,
            status:
              registeredCourses && registeredCourses.includes(course.id)
                ? 'Registered'
                : 'Unregistered',
          },
        ],
        []
      );

      setCourses(result);
    }
  }, [registeredCourses]);

  const hideSnackbarHandler = () => setSnackbarVisibility(false);

  return (
    <AdminLayout>
      <Snackbar
        message={snackbarMessage}
        location={{ vertical: 'top', horizontal: 'center' }}
        type={snackbarType}
        visibility={showSnackbar}
        onClose={hideSnackbarHandler}
      />
      <CloneAssignmentSpecsModal
        sourceCourseID={cloneSourceCourse?.id || ''}
        targetCourseID={cloneTargetCourse?.id || ''}
        visibility={!!cloneSourceCourse && !!cloneTargetCourse}
        onDone={doneCloneModalHandler}
        onClose={() => {
          setCloneSourceCourse(null);
          setCloneTargetCourse(null);
        }}
      />
      <div className="p-4">
        <MaterialTable tableRef={tableRef} {...tableProps} />
      </div>
    </AdminLayout>
  );
};

export default withRouter(Courses);
